import * as React from "react"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { UserPlus } from "lucide-react"
import { toast } from "sonner"

import { AuthLinks, AuthPanel } from "@/components/auth-panel"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { registerFn } from "@/lib/app.functions"

export const Route = createFileRoute("/register")({
  head: () => ({
    meta: [{ title: "Create demo profile · Harbor & Pine Realty" }],
  }),
  component: RegisterPage,
})

function RegisterPage() {
  const register = useServerFn(registerFn)
  const router = useRouter()
  const [pending, setPending] = React.useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPending(true)
    const form = new FormData(event.currentTarget)
    try {
      const password = String(form.get("password") ?? "")
      if (password !== String(form.get("confirmPassword") ?? "")) {
        throw new Error("The passwords do not match.")
      }
      await register({
        data: {
          name: String(form.get("name") ?? ""),
          email: String(form.get("email") ?? ""),
          phone: String(form.get("phone") ?? ""),
          password,
        },
      })
      await router.invalidate()
      toast.success("Demo profile created.")
      await router.navigate({ to: "/dashboard" })
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Unable to create profile."
      )
    } finally {
      setPending(false)
    }
  }

  return (
    <AuthPanel
      eyebrow="Member access"
      title="Create profile"
      intro="Create a disposable demo profile. Use fictional contact details only."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {[
          ["Name", "name", "text", "Demo Explorer"],
          ["Email", "email", "email", "explorer@example.test"],
          ["Phone", "phone", "tel", "+1 555 010 2026"],
          ["Password", "password", "password", ""],
          ["Confirm password", "confirmPassword", "password", ""],
        ].map(([label, name, type, placeholder]) => (
          <div key={name} className="space-y-1.5">
            <Label htmlFor={name} className="field-label">
              {label}
            </Label>
            <Input
              id={name}
              name={name}
              type={type}
              required
              minLength={type === "password" ? 10 : undefined}
              placeholder={placeholder}
              className="field-input"
              autoComplete={
                name === "password" || name === "confirmPassword"
                  ? "new-password"
                  : undefined
              }
            />
          </div>
        ))}
        <Button
          type="submit"
          className="field-button w-full"
          size="lg"
          disabled={pending}
        >
          <UserPlus />
          {pending ? "Creating…" : "Create demo profile"}
        </Button>
      </form>
      <p className="mt-4 text-xs leading-5 text-muted-foreground">
        Shared public demos are not suitable for sensitive data. Profiles may be
        removed without notice.
      </p>
      <AuthLinks mode="register" />
    </AuthPanel>
  )
}
